<template>
  <div class="mockup-display">
    <div class="mockup-display__header">
      <div class="mockup-display__header-title">Jiffy Size Guide</div>
      <div class="mockup-display__close-button" @click="close">
        <UseSvg class="mockup-display__close-button-icon" icon="menu-close" />
      </div>
    </div>
    <div class="mockup-display__content">
      <div class="mockup-display__presets-grid">
        <div v-for="preset in presets" :key="preset" class="mockup-display__presets-grid-item">
          <ShirtMockup
            :artwork-url="artworkUrl"
            :preset="preset"
            :selected-color="selectedColor"
            :size="size"
          />
          <p>{{ presetTitle(preset) }}</p>
        </div>
      </div>
      <PresetMockupColorPicker is-mockup-modal :index="index" />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import PresetMockupColorPicker from "./PresetMockupColorPicker.vue";
import { MAX_PRINT_SIZES_GROUPED } from "../common/constants";
import ShirtMockup from "./ShirtMockup.vue";
import { getTitleCaseString } from "../utils";
import { useDtfStore } from "../store";

const props = defineProps({
  isMobile: {
    type: Boolean,
    default: false
  },
  artworkUrl: {
    type: String,
    default: undefined
  },
  size: {
    type: String,
    default: "small",
    validator: value => ["small", "medium", "large"].includes(value)
  },
  index: {
    type: Number,
    default: 0
  }
});

const store = useDtfStore();

const selectedColor = computed(() => store.presetMockupColor[props.index]);

const emit = defineEmits(["close"]);

const presets = Object.keys(MAX_PRINT_SIZES_GROUPED).filter(key => key !== "custom");

const close = () => {
  emit("close");
};

const presetTitle = preset => getTitleCaseString(preset);
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.mockup-display {
  $this: &;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: $white;
  box-shadow: 0px 15px 20px 0px rgba($black, 0.04);
  border: 1px solid $crafterpay-gray-modern-200;
  border-radius: 8px;
  width: 100vw;

  @include with-tablet {
    width: 680px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 10px 0 10px;
    width: 100%;

    @include with-tablet {
      padding-bottom: 10px;
    }
  }

  &__header-title {
    font-family: $font-family-secondary;
    font-size: 20px;
    font-weight: 700;
    line-height: 20px;
    text-align: left;
  }

  &__close-button {
    padding: 0;
    background-color: transparent;
    color: $text-black;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
  }

  &__close-button-icon {
    width: 30px;
    height: 30px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 10px;

    @include with-desktop {
      padding: 0 10px 10px 10px;
    }
  }

  &__presets-grid {
    margin-top: 10px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 8px;
    overflow-y: auto;
    max-height: 65vh;
    width: 100%;

    @include with-tablet {
      gap: 10px;
      grid-template-columns: repeat(12, 1fr);
      grid-template-rows: repeat(2, 1fr);
      grid-auto-rows: 40px;
      grid-gap: 10px;
    }
  }

  &__presets-grid-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 100%;
    font-weight: 700;
    font-size: 16px;

    @include with-tablet {
      &:nth-child(1) {
        grid-column: 3 / span 4;
      }

      &:nth-child(2) {
        grid-column: 7 / span 4;
      }

      &:nth-child(n + 3) {
        grid-row: 2;
        grid-column: span 4;
      }
    }
  }
}
</style>
